import * as THREE from "three";
import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer";
import { ShaderPass } from "three/examples/jsm/postprocessing/ShaderPass";
import { FXAAShader } from "three/examples/jsm/shaders/FXAAShader";
import Experience from "./Experience";
import { TwoScenePass } from "./TwoScenePass";
import gradeVertexShader from "./shaders/grade/vertex.glsl";
import gradeFragmentShader from "./shaders/grade/fragment.glsl";

const GRADE_RAMP_DURATION = 140;

const GRADE_TARGETS = {
  chroma: 0.01,
  exposure: 0.13,
  contrast: 0,
  lift: 0,
  gamma: 0.05,
  temp: 0.15,
  tint: 0.05,
  saturation: -0.05,
  hue: 0,
  vignette: 1.0,
  grain: 0.025,
};

export default class Renderer {
  constructor(){
    this.experience = new Experience();
    this.sizes = this.experience.sizes;
    this.scene = this.experience.scene;
    this.canvas = this.experience.canvas;
    this.camera = this.experience.camera;
    this.time = this.experience.time;
    this.sequenceBG = this.experience.world.sequenceBG;

    this.setInstance();
    this.setPostProcessing();
  }

  setInstance(){
    this.instance = new THREE.WebGLRenderer({
      canvas: this.canvas,
      antialias: true,
    });
    this.instance.setSize(this.sizes.width, this.sizes.height);
    this.instance.setPixelRatio(this.sizes.pixelRatio);

    this.instance.outputColorSpace = THREE.LinearSRGBColorSpace;
  }

  setPostProcessing(){
    this.composer = new EffectComposer(this.instance);
    this.composer.setSize(this.sizes.width, this.sizes.height);
    this.composer.setPixelRatio(this.sizes.pixelRatio);

    // Renders the plate scene then the main scene with a depth clear
    // between (see TwoScenePass) — replaces a plain RenderPass since we
    // need two full scene/camera pairs composited into one buffer.
    this.renderPass = new TwoScenePass(
      this.sequenceBG.bgScene,
      this.sequenceBG.bgCamera,
      this.scene,
      this.camera.instance,
      this.experience.debug,
    );
    this.composer.addPass(this.renderPass);

    // FXAA runs first in the reference pipeline, before chroma/bloom/grade.
    this.fxaaPass = new ShaderPass(FXAAShader);
    this.composer.addPass(this.fxaaPass);

    this.gradePass = new ShaderPass({
      uniforms: {
        tDiffuse: { value: null },
        uTexel: { value: new THREE.Vector2() },
        uTime: { value: 0 },
        uChromaAmt: { value: 0 },
        uBloomAmt: { value: 0.1 },
        uBloomThresh: { value: 0 },
        uExposure: { value: 0 },
        uContrast: { value: 0 },
        uLift: { value: 0 },
        uGamma: { value: 0 },
        uTemp: { value: 0 },
        uTint: { value: 0 },
        uSat: { value: 0 },
        uHue: { value: 0 },
        uVig: { value: 0 },
        uVigR: { value: 0.6 },
        uVigSoft: { value: 1.0 },
        uGrain: { value: 0 },
      },
      vertexShader: gradeVertexShader,
      fragmentShader: gradeFragmentShader,
    });
    this.composer.addPass(this.gradePass);
    this.gradePass.renderToScreen = true;

    this.updateResolutionUniforms();

    this.gradeRampTicks = 0;

    this.wrapPassesForDebug();
  }

  // Dev-only: wraps each composer pass's render() with a Debug timing
  // bracket, so the overlay can show which pass is actually costing frame
  // time instead of just one opaque composer.render() total. TwoScenePass
  // times its own plate/main-scene split internally (see TwoScenePass.js),
  // so it's not wrapped again here.
  wrapPassesForDebug(){
    const debug = this.experience.debug;
    if (!debug) return;

    const labeled = [
      ["render:fxaa", this.fxaaPass],
      ["render:grade", this.gradePass],
    ];

    for (const [label, pass] of labeled) {
      const originalRender = pass.render.bind(pass);
      pass.render = (...args) => {
        debug.begin(label);
        originalRender(...args);
        debug.end(label);
      };
    }
  }

  updateResolutionUniforms(){
    const pixelRatio = this.instance.getPixelRatio();
    const w = this.sizes.width * pixelRatio;
    const h = this.sizes.height * pixelRatio;

    this.fxaaPass.material.uniforms["resolution"].value.set(1 / w, 1 / h);
    this.gradePass.uniforms.uTexel.value.set(1 / w, 1 / h);
  }

  resize(){
    this.instance.setSize(this.sizes.width, this.sizes.height);
    this.instance.setPixelRatio(this.sizes.pixelRatio);

    this.composer.setSize(this.sizes.width, this.sizes.height);
    this.composer.setPixelRatio(this.sizes.pixelRatio);
    this.updateResolutionUniforms();
  }

  // Pre-links shader programs for every material currently in the scene —
  // including invisible meshes, which WebGLRenderer otherwise defers
  // compiling until their first real draw call. Called once from
  // Experience.init() after TextCard/CouplesRotation's own async setup
  // (font load / atlas build) has finished, so their program link happens
  // here, off the reveal frame, instead of on the tick
  // showCouplesRotation()/the text fade-in first flips them visible.
  //
  // compile() alone did NOT fix the ~700ms spike (confirmed by re-profiling
  // after adding it — timing unchanged) — it explicitly does not touch
  // textures. The real cost is CouplesRotation's atlas: 46 frames packed
  // into one canvas at up to ~4096px square (cellCap 1536, capped by
  // maxTextureSize/cols) is a many-megapixel CanvasTexture, and its GPU
  // upload (gl.texImage2D) was what WebGLRenderer was deferring to that
  // first draw call. initTexture() forces that upload explicitly, ahead of
  // time, which compile() does not do.
  warmup(textures = []){
    this.instance.compile(this.scene, this.camera.instance);
    for (const texture of textures) {
      if (texture) this.instance.initTexture(texture);
    }
  }

  update(){
    this.gradeRampTicks = Math.min(this.gradeRampTicks + 1, GRADE_RAMP_DURATION);
    const t = this.gradeRampTicks / GRADE_RAMP_DURATION;

    const u = this.gradePass.uniforms;
    u.uChromaAmt.value = GRADE_TARGETS.chroma * t;
    u.uExposure.value = GRADE_TARGETS.exposure * t;
    u.uContrast.value = GRADE_TARGETS.contrast * t;
    u.uLift.value = GRADE_TARGETS.lift * t;
    u.uGamma.value = GRADE_TARGETS.gamma * t;
    u.uTemp.value = GRADE_TARGETS.temp * t;
    u.uTint.value = GRADE_TARGETS.tint * t;
    u.uSat.value = GRADE_TARGETS.saturation * t;
    u.uHue.value = GRADE_TARGETS.hue * t;
    u.uVig.value = GRADE_TARGETS.vignette * t;
    u.uTime.value += this.time.delta / 1000;

    const videoFade = this.experience.world.videoTextDom?.visibility ?? 0;
    const coupleHold = Math.max(0, 1 - Math.min(1, videoFade / 0.2));
    u.uGrain.value = GRADE_TARGETS.grain * t * coupleHold;

    this.composer.render();

    // EpisodeSlider's WebGL card layer (EpisodeSliderGL) renders as a
    // direct extra pass on top of everything, in its own scene/camera,
    // deliberately bypassing the composer's grade/FXAA pipeline entirely
    // — the slider is a flat UI element (per the Figma), not part of the
    // cinematic film grade. Same autoClear/clearDepth shape TwoScenePass
    // already uses internally for its own two-scene composite. Short-
    // circuits to a no-op before the finale constructs episodeSlider at
    // all, and again until EpisodeSlider.reveal() flips it visible.
    const sliderGL = this.experience.world.episodeSlider?.gl;
    if (sliderGL?.visible) {
      this.instance.autoClear = false;
      this.instance.clearDepth();
      this.instance.render(sliderGL.scene, sliderGL.camera);
      this.instance.autoClear = true;
    }
  }
}
